import React, { useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, ButtonSize, PagePanel } from 'edenred-ui';
import { useForm, FormProvider } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { FormFields } from '@epi-forms/helpers';
import { registeredVoucherOrderAction } from '@epi-store/registeredVoucherOrder/registeredVoucherOrder.slice';
import { getOrderData } from '@epi-store/registeredVoucherOrder/registeredVoucherOrder.selectors';
import { goToPage } from '@epi-actions/navigation';
import { IOrderAmount, IVouchers } from '@epi-models/containers/OrderAmount';
import { getApplicationLocale } from '@epi-store/application/application.selectors';
import { getAvailableVouchersByType } from '@epi-selectors/api';
import {
  getVoucherTypeKey,
  getFaceValueChangedData,
  getVoucherReorderDetails
} from '@epi-selectors/voucherReorder';
import {
  getMaxOrderTotalAmount,
  getVoucherMinimumQuantity
} from '@epi-selectors/settings';
import { VoucherTypes } from '@epi-constants/voucherTypes';
import { routerPaths } from '@epi-constants/routerPaths';
import { NavigationHeader } from '@epi-components/NavigationHeader/NavigationHeader';

import { BenefitSummaryContent } from '../../components/BenefitSummaryContent/BenefitSummaryContent';
import * as voucherReorderActions from '../../actions/voucherReorder';
import * as apiActions from '../../actions/api';
import * as navigationActions from '../../actions/navigation';
import { formatCurrency } from '../../helpers/numeral';
import { orderSummary as orderSummarySelector } from '../../selectors/voucherOrder';
import { FormConstants as forms } from '../../constants/forms';
import { VoucherQuantity } from '../../forms/VoucherQuantity/VoucherQuantity';
import './OrderAmount.scss';

interface FormType {
  [FormFields.VoucherQuantity]: number;
  [FormFields.VoucherId]: IVouchers['id'];
  [FormFields.VoucherWorth]: IVouchers['value'];
}

function OrderAmount({
  vouchers,
  orderSummary,
  voucherTypeKey,
  locale,
  faceValueChangingInfo,
  maxOrderTotalAmount,
  voucherMinimumQuantity,
  anyTouched = false
}: IOrderAmount) {
  const { orderId } = useParams<{ orderId: string }>();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const orderData = useSelector(getOrderData);

  const form = useForm<FormType>({
    mode: 'onChange',
    defaultValues: {
      [FormFields.VoucherQuantity]: 0,
      [FormFields.VoucherId]: '',
      [FormFields.VoucherWorth]: 0
    }
  });
  const { handleSubmit, watch, setValue } = form;
  const voucherIdValue = watch(FormFields.VoucherId);

  useEffect(() => {
    setValue(FormFields.VoucherQuantity, orderData.totalQuantity);
    setValue(FormFields.VoucherId, orderData.voucherId);
    setValue(FormFields.VoucherWorth, orderData.faceValue);
  }, []);

  useEffect(() => {
    const vouchersValue = (vouchers || []).find(v => v.id === voucherIdValue);
    if (vouchersValue) {
      setValue(FormFields.VoucherWorth, vouchersValue.value);
    }
  }, [voucherIdValue]);

  const onSubmit = values => {
    dispatch(
      registeredVoucherOrderAction.addOrderInformation({
        totalQuantity: Number(values[FormFields.VoucherQuantity]),
        voucherId: values[FormFields.VoucherId],
        faceValue: values[FormFields.VoucherWorth]
      })
    );
    dispatch(
      goToPage(routerPaths.voucherReorder, {
        params: {
          voucherType: VoucherTypes[voucherTypeKey],
          orderId
        }
      })
    );
  };

  const useCalculator = VoucherTypes[voucherTypeKey] === VoucherTypes.Lunch;

  return (
    <NavigationHeader
      withBackButton
      title={t('containers.edit_order_amount.title')}
    >
      <FormProvider {...form}>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <PagePanel
            className="OrderAmount"
            title={t(`containers.voucher_order.header`)}
          >
            <VoucherQuantity
              voucherTypeKey={voucherTypeKey}
              vouchers={vouchers}
              useCalculator={useCalculator}
              locale={locale}
              maxOrderTotalAmount={maxOrderTotalAmount}
              voucherMinimumQuantity={voucherMinimumQuantity}
              newFaceValueMessage={
                (faceValueChangingInfo &&
                  !anyTouched &&
                  t('forms.voucher_quantity.voucher_face_value_changed', {
                    oldValue: formatCurrency(faceValueChangingInfo.oldValue),
                    newValue: formatCurrency(faceValueChangingInfo.newValue)
                  })) ||
                undefined
              }
            />
            <hr />
            <BenefitSummaryContent
              voucherTypeKey={voucherTypeKey}
              orderSummary={orderSummary}
            />
          </PagePanel>
          <Button
            id="order-amount-submit-btn"
            type="submit"
            size={ButtonSize.Medium}
            className="pull-right"
          >
            {t('containers.edit_order_amount.update_amount')}
          </Button>
        </form>
      </FormProvider>
    </NavigationHeader>
  );
}

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    { ...voucherReorderActions, ...apiActions, ...navigationActions },
    dispatch
  );

const mapStateToProps = state => ({
  locale: getApplicationLocale(state),
  voucherTypeKey: getVoucherTypeKey(state),
  vouchers: getAvailableVouchersByType(getVoucherTypeKey(state))(state),
  faceValueChangingInfo: getFaceValueChangedData(state),
  orderSummary: orderSummarySelector(forms.EDIT_VOUCHER_QUANTITY_FORM)(state),
  initialValues: {
    voucher_details: getVoucherReorderDetails(state)
  },
  maxOrderTotalAmount: getMaxOrderTotalAmount(state),
  voucherMinimumQuantity: getVoucherMinimumQuantity(state)
});

const ConnectedOrderAmount = connect(
  mapStateToProps,
  mapDispatchToProps
)(OrderAmount);

export { ConnectedOrderAmount as OrderAmount };
